import React, { useEffect, useRef, useCallback } from "react";
import "./SingleProduct.scss";
import { theme, Layout, Image, Skeleton, notification, Segmented } from "antd";
import {
  SingleProductHeader,
  ProductSIze,
  RelatedProducts,
  ProductReviews,
} from "./components";
import {
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router";
import classNames from "classnames";
import useWindowWidth from "../../hooks/useWindowWidth";
import { getMenuItem } from "../../features/menuItems/menuItemsAction";
import { resetMenuItemsError } from "../../features/menuItems/menuItemsSlice";
import { useDispatch, useSelector } from "react-redux";
import product_logo from "../../assets/logo_product.svg";
import { getRelatedProducts } from "../../features/relatedProducts/relatedProductsAction";
import { CustomEmpty, VideoPlayer } from "../../components";
import Utils from "../../utils";

const { Content } = Layout;

export default function SingleProduct() {
  const { useToken } = theme;
  const { token } = useToken();
  const [api, contextHolder] = notification.useNotification();
  const openNotificationWithIcon = useCallback(
    (type, title, text) => {
      api[type]({
        title: title,
        description: Utils.getErrorText(text),
        placement: "top",
      });
    },
    [api]
  );
  const { isMobile, isMiddlesDesktop } = useWindowWidth();
  const location = useLocation();

  const { productId } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { loading, singleProduct, error } = useSelector(
    (state) => state?.menuItems
  );
  const itemReviewsCount = useSelector(
    (state) => state?.reviews?.itemReviewsMeta?.count
  );
  const [searchParams, setSearchParams] = useSearchParams();

  const singleProductHeaderRef = useRef(null);
  const segements = [
    {
      label: "Product Description",
      value: "product_description",
    },
    {
      label: itemReviewsCount
        ? `Reviews (${itemReviewsCount})`
        : "Reviews",
      value: "review",
    },
  ];
  // The active tab lives in the URL so a reload or a shared link restores
  // it; the default Product Description tab is just no param.
  const selectedSegment =
    searchParams?.get("tab") === "reviews"
      ? "review"
      : "product_description";

  const onSegmentChange = (val) => {
    setSearchParams(
      Utils?.get_filtered_url_params(searchParams, {
        tab: val === "review" ? "reviews" : undefined,
      }),
      // Replace instead of push so the back arrow leaves the page instead
      // of crawling through tab switches — same as the MyReviews filters.
      { replace: true }
    );
  };

  const getContent = (segment) => {
    switch (segment) {
      case "product_description":
        return !singleProduct?.description && !loading ? (
          <CustomEmpty
            description="No Description"
            circleColor={
              token?.icons?.emptyCircleIconColor
                ? token?.icons?.emptyCircleIconColor
                : ""
            }
            boxBorderColor={
              token?.icons?.emptyIconBoxBorderColor
                ? token?.icons?.emptyIconBoxBorderColor
                : ""
            }
          />
        ) : (
          <div
            className="singleProduct-cont__additionalInfo__middleText"
            style={{
              color: token?.Typography?.mainColorText,
              fontFamily: token?.fontFamily,
            }}
            dangerouslySetInnerHTML={
              loading
                ? {
                    __html: "",
                  }
                : {
                    __html: singleProduct?.description,
                  }
            }
          />
        );
      default:
        return "";
    }
  };
  useEffect(() => {
    const parentCategory = Utils?.findParentCategory(location?.state);
    dispatch(getMenuItem({ id: productId }));
    dispatch(
      getRelatedProducts({
        page: 1,
        per_page: 25,
        category: parentCategory ? parentCategory?.name : undefined,
        count__gt: 0,
      })
    );
  }, [dispatch, productId, location?.state]);

  useEffect(() => {
    if (location?.pathname?.includes("product")) {
      if (error && !loading) {
        for (let key in error) {
          openNotificationWithIcon("error", key, error[key]);
        }
        dispatch(resetMenuItemsError());
      }
    }
  }, [error, loading, location?.pathname, openNotificationWithIcon, dispatch]);

  return (
    <>
      {contextHolder}
      <Content
        className="singleProduct-cont"
        style={{
          backgroundColor: token?.Layout?.contentBg,
        }}>
        <SingleProductHeader
          token={token}
          singleProductHeaderRef={singleProductHeaderRef}
          loading={loading}
          product={singleProduct}
          navigate={() => {
            navigate(-1);
          }}
        />
        <div className="singleProduct-cont__detailsWrapper">
          <div
            className="singleProduct-cont__detailsWrapper__images"
            style={
              !singleProduct?.picture_url &&
              !(singleProduct?.video_ready && singleProduct?.video_hls_url) &&
              !loading
                ? {
                    background: token?.product_default_image_bg,
                    borderRadius: "8px",
                  }
                : null
            }>
            {loading ? (
              <Skeleton.Image
                active={true}
                style={{
                  width: "100%",
                  height: isMobile
                    ? "370px"
                    : isMiddlesDesktop
                    ? "430px"
                    : "569px",
                  backgroundColor: token?.Card?.colorBgCard,
                  borderRadius: "8px",
                }}
              />
            ) : singleProduct?.video_ready && singleProduct?.video_hls_url ? (
              <div
                className="singleProduct-cont__detailsWrapper__images__video"
                style={{
                  height: isMobile
                    ? "370px"
                    : isMiddlesDesktop
                    ? "430px"
                    : "569px",
                }}>
                <VideoPlayer
                  hlsUrl={singleProduct?.video_hls_url}
                  posterUrl={singleProduct?.video_thumbnail_url}
                  ariaLabel={singleProduct?.name || "Product video"}
                />
              </div>
            ) : (
              <Image
                alt="product_big_image"
                src={
                  singleProduct?.picture_url
                    ? singleProduct?.picture_url
                    : product_logo
                }
                width="100%"
                height={
                  isMobile ? "370px" : isMiddlesDesktop ? "430px" : "569px"
                }
                style={
                  !singleProduct?.picture_url
                    ? {
                        width: "100px",
                        borderRadius: "8px",
                      }
                    : {
                        borderRadius: "8px",
                      }
                }
              />
            )}
          </div>
          <ProductSIze
            token={token}
            product={singleProduct}
            loading={loading}
          />
        </div>
        <div
          className={classNames("singleProduct-cont__additionalInfo", {
            // The reviews list spans the page like the design; the
            // description keeps its original narrow measure.
            "singleProduct-cont__additionalInfo--wide":
              selectedSegment === "review",
          })}>
          <Segmented
            className="singleProduct-cont__additionalInfo__segmented"
            disabled={loading}
            options={segements}
            value={selectedSegment}
            onChange={onSegmentChange}
          />
          {selectedSegment === "product_description"
            ? getContent(selectedSegment)
            : null}
          {/* Mounted on both tabs (hidden on the description one) so the
              Reviews tab count is known before the first switch. */}
          <ProductReviews
            token={token}
            productId={productId}
            hidden={selectedSegment !== "review"}
          />
        </div>
        <RelatedProducts
          token={token}
          singleProductHeaderRef={singleProductHeaderRef}
        />
      </Content>
    </>
  );
}
